/**
 * 🐛 HQ 오류 게시판
 * 전체 매장이 올린 오류 제보를 열람·필터하고, 상태변경·본부답변을 남긴다.
 * Claude 진단(🤖)이 있으면 함께 표시한다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { hqAuthService } from '../services/hqAuthService';
import {
  hqErrorReportService,
  HqErrorReportListItem,
  HqErrorReportDetail,
} from '../services/hqErrorReportService';

const STATUS_LIST: { value: string; label: string; cls: string }[] = [
  { value: 'open', label: '접수', cls: 'bg-amber-100 text-amber-800' },
  { value: 'reviewing', label: '확인중', cls: 'bg-blue-100 text-blue-800' },
  { value: 'in_progress', label: '수정중', cls: 'bg-orange-100 text-orange-800' },
  { value: 'resolved', label: '해결', cls: 'bg-emerald-100 text-emerald-800' },
  { value: 'not_a_bug', label: '정상(착각)', cls: 'bg-slate-200 text-slate-700' },
  { value: 'need_info', label: '정보부족', cls: 'bg-rose-100 text-rose-800' },
];
const statusMeta = (s: string) => STATUS_LIST.find((x) => x.value === s) || { value: s, label: s, cls: 'bg-slate-200 text-slate-700' };

const VERDICT: Record<string, { label: string; cls: string }> = {
  bug: { label: '🐛 실제 오류', cls: 'bg-rose-100 text-rose-800' },
  not_a_bug: { label: '✅ 정상(착각)', cls: 'bg-slate-200 text-slate-700' },
  need_info: { label: '❓ 정보 부족', cls: 'bg-amber-100 text-amber-800' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const m = statusMeta(status);
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>{m.label}</span>;
};

/** 인증 필요한 첨부 이미지 (blob 로 로드) */
const AuthImg: React.FC<{ reportId: string; attId: string }> = ({ reportId, attId }) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    hqErrorReportService.attachmentObjectUrl(reportId, attId).then((u) => {
      if (!alive) { URL.revokeObjectURL(u); return; }
      url = u; setSrc(u);
    }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [reportId, attId]);
  if (!src) return <div className="w-40 h-28 bg-slate-100 rounded animate-pulse" />;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img src={src} alt="screenshot" className="max-w-[240px] max-h-[200px] rounded border border-slate-200 object-contain" />
    </a>
  );
};

const HqErrorReportsPage: React.FC = () => {
  const isAdmin = hqAuthService.getCachedUser()?.role === 'hq_admin';
  const [list, setList] = useState<HqErrorReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');

  const [selected, setSelected] = useState<HqErrorReportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editReply, setEditReply] = useState('');
  const [editMemo, setEditMemo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setList(await hqErrorReportService.list({ status: statusFilter || undefined, q: q || undefined }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setSelected({ id } as HqErrorReportDetail);
    try {
      const d = await hqErrorReportService.detail(id);
      setSelected(d);
      setEditStatus(d.status);
      setEditReply(d.admin_reply || '');
      setEditMemo(d.hq_memo || '');
    } catch (e) {
      console.error(e);
      alert('상세를 불러오지 못했습니다.');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await hqErrorReportService.update(selected.id, {
        status: editStatus,
        admin_reply: editReply,
        hq_memo: editMemo,
      });
      setSelected(null);
      await load();
    } catch (e: any) {
      alert(`저장 실패: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">🐛 오류 게시판</h1>
          <p className="text-sm text-slate-500 mt-1">매장에서 올라온 오류 제보 · 🤖 표시는 Claude 자동 진단 결과입니다.</p>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg">새로고침</button>
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setStatusFilter('')}
          className={`px-3 py-1 rounded-full text-sm ${statusFilter === '' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>전체</button>
        {STATUS_LIST.map((s) => (
          <button key={s.value} onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1 rounded-full text-sm ${statusFilter === s.value ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>{s.label}</button>
        ))}
        <div className="flex-1" />
        <form onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }} className="flex flex-wrap gap-2">
          <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="제목·내용 검색"
            className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg flex-1 min-w-[160px] sm:w-52 sm:flex-none" />
          <button className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg">검색</button>
        </form>
      </div>

      {/* 목록 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-400">불러오는 중…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-slate-400">제보가 없습니다.</div>
        ) : (
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">매장</th>
                <th className="px-4 py-3 font-medium">제목</th>
                <th className="px-4 py-3 font-medium">제보자</th>
                <th className="px-4 py-3 font-medium">진단</th>
                <th className="px-4 py-3 font-medium">상태</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">일시</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} onClick={() => openDetail(r.id)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-3 whitespace-nowrap">{r.store_name}</td>
                  <td className="px-4 py-3">
                    {r.title}
                    {r.attachment_count > 0 && <span className="ml-2 text-xs text-slate-400">📷{r.attachment_count}</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-500">{r.reporter_name || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.claude_verdict && VERDICT[r.claude_verdict] ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${VERDICT[r.claude_verdict].cls}`}>
                        {VERDICT[r.claude_verdict].label}
                      </span>
                    ) : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-400 text-xs">
                    {new Date(r.created_at).toLocaleString('ko-KR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 상세 모달 */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !selected.title ? (
              <div className="p-10 text-center text-slate-400">불러오는 중…</div>
            ) : (
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs text-slate-400">{selected.store_name} · {selected.reporter_name || '-'}</div>
                    <h2 className="text-xl font-bold text-slate-800 mt-1">{selected.title}</h2>
                    <div className="flex items-center gap-2 mt-2">
                      <StatusBadge status={selected.status} />
                      {selected.category && <span className="text-xs text-slate-500">{selected.category}</span>}
                      <span className="text-xs text-slate-400">{new Date(selected.created_at).toLocaleString('ko-KR')}</span>
                    </div>
                  </div>
                  <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
                </div>

                {selected.content && (
                  <p className="mt-4 whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">{selected.content}</p>
                )}

                {/* 스크린샷 */}
                {selected.attachments?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold text-slate-500 mb-2">스크린샷</div>
                    <div className="flex flex-wrap gap-3">
                      {selected.attachments.map((a) => <AuthImg key={a.id} reportId={selected.id} attId={a.id} />)}
                    </div>
                  </div>
                )}

                {/* 🤖 Claude 진단 */}
                {(selected.claude_verdict || selected.claude_analysis) && (
                  <div className="mt-4 bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-bold text-indigo-800">🤖 Claude 진단</span>
                      {selected.claude_verdict && VERDICT[selected.claude_verdict] && (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${VERDICT[selected.claude_verdict].cls}`}>
                          {VERDICT[selected.claude_verdict].label}
                        </span>
                      )}
                      {selected.claude_checked_at && (
                        <span className="text-xs text-indigo-400">{new Date(selected.claude_checked_at).toLocaleString('ko-KR')}</span>
                      )}
                    </div>
                    {selected.claude_analysis && (
                      <p className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">{selected.claude_analysis}</p>
                    )}
                  </div>
                )}

                {/* 자동 수집 정보 */}
                {(selected.page_url || selected.app_context) && (
                  <details className="mt-4 text-xs text-slate-500">
                    <summary className="cursor-pointer select-none">자동 수집 정보 (디버깅용)</summary>
                    <div className="mt-2 bg-slate-50 rounded-lg p-3 break-all">
                      {selected.page_url && <div>URL: {selected.page_url}</div>}
                      {selected.app_context && <pre className="whitespace-pre-wrap mt-1">{JSON.stringify(selected.app_context, null, 2)}</pre>}
                    </div>
                  </details>
                )}

                {/* 처리 (관리자만) */}
                <div className="mt-6 border-t border-slate-100 pt-4">
                  {isAdmin ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 block mb-1">상태 변경</label>
                        <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}
                          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
                          {STATUS_LIST.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 block mb-1">점주에게 보이는 답변</label>
                        <textarea value={editReply} onChange={(e) => setEditReply(e.target.value)} rows={2}
                          placeholder="예: 확인 후 수정했습니다. / 정상 동작이며 이렇게 사용하시면 됩니다."
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 block mb-1">본부 내부 메모 (점주 비노출)</label>
                        <textarea value={editMemo} onChange={(e) => setEditMemo(e.target.value)} rows={2}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setSelected(null)} className="px-4 py-2 text-sm bg-slate-100 rounded-lg">닫기</button>
                        <button onClick={save} disabled={saving}
                          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-60">
                          {saving ? '저장 중…' : '저장'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">
                      {selected.admin_reply && (
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                          <div className="font-semibold text-blue-800 mb-1">💬 본부 답변</div>
                          <div className="whitespace-pre-wrap">{selected.admin_reply}</div>
                        </div>
                      )}
                      <div className="text-xs text-slate-400 mt-2">상태 변경은 관리자만 가능합니다.</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HqErrorReportsPage;
